import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, Trash2, Plus, Users } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", email: "", contact_name: "", phone: "", address: "", notes: "", default_template_id: "", order_prefix: "", next_order_number: 1, client_code: "" };

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => Promise.all([api.listSuppliers(), api.listTemplates()]).then(([s, t]) => { setItems(s); setTemplates(t); });
  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    const prefix = (form.order_prefix || "").trim().toUpperCase();
    if (!prefix) {
      toast.error("El prefijo de pedido es obligatorio");
      return;
    }
    try {
      const payload = {
        ...form,
        order_prefix: prefix,
        next_order_number: parseInt(form.next_order_number) || 1,
        default_template_id: form.default_template_id || null,
      };
      if (editId) {
        await api.updateSupplier(editId, payload);
        toast.success("Proveedor actualizado");
      } else {
        await api.createSupplier(payload);
        toast.success("Proveedor creado");
      }
      setOpen(false); setForm(empty); setEditId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const onEdit = (s) => {
    setForm({
      name: s.name, email: s.email, contact_name: s.contact_name || "", phone: s.phone || "",
      address: s.address || "", notes: s.notes || "", default_template_id: s.default_template_id || "",
      order_prefix: s.order_prefix || "", next_order_number: s.next_order_number || 1,
      client_code: s.client_code || "",
    });
    setEditId(s.id); setOpen(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar proveedor?")) return;
    await api.deleteSupplier(id);
    toast.success("Proveedor eliminado");
    load();
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 md:mb-8">
        <div>
          <p className="text-[10px] text-[#5A5D6E] uppercase tracking-[0.2em] font-medium">Catálogo</p>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight mt-2">Proveedores</h1>
          <p className="text-sm text-[#5A5D6E] mt-2">{items.length} proveedor{items.length !== 1 ? "es" : ""} registrado{items.length !== 1 ? "s" : ""}.</p>
        </div>
        <Button
          onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}
          className="bg-[#002FA7] hover:bg-[#002FA7]/90 rounded-sm w-full md:w-auto"
          data-testid="supplier-new-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuevo proveedor
        </Button>
      </div>

      <div className="bg-white border border-[#E2E4E9] rounded-sm overflow-x-auto">
        {items.length === 0 ? (
          <div className="py-16 text-center">
            <Users className="w-12 h-12 text-[#E2E4E9] mx-auto" strokeWidth={1.25} />
            <p className="text-sm text-[#5A5D6E] mt-3">Sin proveedores aún</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F7F7F9] border-b border-[#E2E4E9]">
              <tr>
                <th className="text-left p-3 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Nombre</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Email</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Prefijo</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Cód. cliente</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Contacto</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Teléfono</th>
                <th className="w-24 p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-b border-[#E2E4E9] hover:bg-[#F7F7F9]" data-testid={`supplier-row-${s.id}`}>
                  <td className="p-3 font-medium">{s.name}</td>
                  <td className="p-3 font-mono text-xs text-[#5A5D6E]">{s.email}</td>
                  <td className="p-3 font-mono text-xs">
                    {s.order_prefix ? (
                      <span>{s.order_prefix}-{String(s.next_order_number || 1).padStart(5, '0')}</span>
                    ) : (
                      <span className="text-[#D32F2F]">Sin prefijo</span>
                    )}
                  </td>
                  <td className="p-3 font-mono text-xs text-[#5A5D6E]">{s.client_code || "—"}</td>
                  <td className="p-3 text-[#5A5D6E]">{s.contact_name || "—"}</td>
                  <td className="p-3 text-[#5A5D6E]">{s.phone || "—"}</td>
                  <td className="p-3 flex gap-1 justify-end">
                    <button onClick={() => onEdit(s)} className="p-1.5 hover:bg-[#F3F4F6] rounded-sm" data-testid={`supplier-edit-${s.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => onDelete(s.id)} className="p-1.5 hover:bg-[#D32F2F]/10 text-[#D32F2F] rounded-sm" data-testid={`supplier-delete-${s.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="supplier-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editId ? "Editar proveedor" : "Nuevo proveedor"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Nombre *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="supplier-input-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="supplier-input-email" />
              </div>
              <div>
                <Label>Contacto</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="supplier-input-contact" />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="supplier-input-phone" />
              </div>
            </div>
            <div>
              <Label>Dirección</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="supplier-input-address" />
            </div>
            <div>
              <Label>Código de cliente <span className="text-[#5A5D6E] text-xs normal-case">(el que tu proveedor usa para identificarte)</span></Label>
              <Input
                value={form.client_code}
                onChange={(e) => setForm({ ...form, client_code: e.target.value })}
                placeholder="C-90215"
                className="font-mono"
                data-testid="supplier-input-client-code"
              />
              <p className="text-xs text-[#5A5D6E] mt-1">Aparecerá arriba a la derecha en el PDF.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Prefijo de pedido <span className="text-[#D32F2F]">*</span></Label>
                <Input
                  value={form.order_prefix}
                  onChange={(e) => setForm({ ...form, order_prefix: e.target.value.toUpperCase() })}
                  placeholder="DAN, OFI, CCL…"
                  maxLength={20}
                  required
                  className="font-mono uppercase"
                  data-testid="supplier-input-prefix"
                />
                <p className="text-xs text-[#5A5D6E] mt-1">Se usará como <span className="font-mono">{form.order_prefix || "DAN"}-00001</span></p>
              </div>
              <div>
                <Label>Próximo número</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.next_order_number}
                  onChange={(e) => setForm({ ...form, next_order_number: e.target.value })}
                  className="font-mono"
                  data-testid="supplier-input-next-number"
                />
                <p className="text-xs text-[#5A5D6E] mt-1">Empezar desde este número</p>
              </div>
            </div>
            <div>
              <Label>Plantilla por defecto</Label>
              {(() => {
                const tplOptions = templates.filter((t) => t.supplier_id === editId);
                return (
                  <Select
                    value={form.default_template_id || "__none__"}
                    onValueChange={(v) => setForm({ ...form, default_template_id: v === "__none__" ? "" : v })}
                    disabled={!editId}
                  >
                    <SelectTrigger data-testid="supplier-default-template">
                      <SelectValue placeholder={editId ? (tplOptions.length ? "Sin plantilla por defecto" : "No hay plantillas para este proveedor") : "Guardá el proveedor primero"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin plantilla por defecto</SelectItem>
                      {tplOptions.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.items?.length || 0} items)</SelectItem>)}
                    </SelectContent>
                  </Select>
                );
              })()}
              {!editId && <p className="text-xs text-[#5A5D6E] mt-1">Disponible al editar después de crear.</p>}
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="supplier-input-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancelar</Button>
              <Button type="submit" className="bg-[#002FA7] hover:bg-[#002FA7]/90 rounded-sm" data-testid="supplier-save-button">
                {editId ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
